import {
  DischargeDecisionFactor,
  DischargeDisposition,
  DischargePatient,
  DischargeProgramFit,
  DISPOSITION_LABELS,
} from '~/types/discharge';

const ALL_DISPOSITIONS: DischargeDisposition[] = [
  'home_health', 'hospital_at_home', 'snf_at_home', 'hospice', 'infusion_at_home', 'palliative',
];

function hashOf(id: string): number {
  return Array.from(id).reduce((acc, c) => acc + c.charCodeAt(0), 0);
}

function hoursAgoIso(hours: number): string {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

// Deterministic per-(patient, disposition) factor list. The recommended
// disposition gets predominantly positive factors; other dispositions get a
// mix that leans negative as their score drops.
function buildFactorsForDisposition(
  patient: DischargePatient,
  disposition: DischargeDisposition,
  score: number,
): DischargeDecisionFactor[] {
  const h = hashOf(patient.id + disposition);
  const isRecommended = disposition === patient.recommendedDisposition;

  // Bias contribution: high scores favor positive factors
  const positiveBias = score >= 80 ? 0.8 : score >= 60 ? 0.5 : 0.25;
  const pickContribution = (idx: number): 'positive' | 'negative' =>
    ((h + idx * 7) % 100) / 100 < positiveBias ? 'positive' : 'negative';

  const baseFactors: Array<Omit<DischargeDecisionFactor, 'id' | 'timestamp' | 'contribution'>> = [
    {
      label: 'Caregiver Support',
      importance: 70 + (h % 25),
      value: isRecommended ? 'Engaged primary contact confirmed' : 'Caregiver availability uncertain',
      dataSource: 'Social Work Assessment',
    },
    {
      label: 'Insurance Eligibility',
      importance: 60 + (h % 30),
      value: `${patient.insurance} coverage verified`,
      dataSource: 'Benefits Verification',
    },
    {
      label: 'Clinical Stability',
      importance: 55 + (h % 35),
      value: isRecommended ? 'Hemodynamically stable, no acute changes' : 'Periodic instability noted',
      dataSource: 'Nursing Assessment',
    },
    {
      label: 'Functional Status',
      importance: 50 + (h % 40),
      value: isRecommended ? 'Independent with mobility aids' : 'Requires assistance with ADLs',
      dataSource: 'PT/OT Evaluation',
    },
    {
      label: `${DISPOSITION_LABELS[disposition]} Program Fit`,
      importance: 65 + (h % 30),
      value: isRecommended
        ? 'Clinical profile aligns with program criteria'
        : 'Some program criteria not fully met',
      dataSource: 'Care Coordination',
    },
  ];

  return baseFactors.map((f, idx) => ({
    ...f,
    id: `${patient.id}-${disposition}-f${idx}`,
    timestamp: hoursAgoIso(3 + ((h + idx * 5) % 36)),
    contribution: pickContribution(idx),
  }));
}

export function buildProgramFit(patient: DischargePatient): DischargeProgramFit {
  const h = hashOf(patient.id);
  const recommended = patient.recommendedDisposition;
  const explicitScores = patient.programScores;
  const explicitFactors = patient.decisionFactors;

  // Generated fallback spread: the recommended disposition keeps the patient's
  // dispositionScore, and the others fall off by 8-20 points each in a
  // deterministic-but-varied way.
  const others = ALL_DISPOSITIONS.filter(d => d !== recommended)
    .sort((a, b) => hashOf(patient.id + a) - hashOf(patient.id + b));
  const genScore = { [recommended]: patient.dispositionScore } as Record<DischargeDisposition, number>;
  others.forEach((d, i) => {
    const drop = 12 + ((h + i * 11) % 16);
    genScore[d] = Math.max(20, patient.dispositionScore - drop * (i + 1));
  });

  // When the patient carries an explicit fit, surface exactly the dispositions
  // it covers (their real, ranked set); otherwise the full generated spread.
  const dispositions: DischargeDisposition[] = explicitScores
    ? ALL_DISPOSITIONS.filter(d => explicitScores[d] != null)
    : ALL_DISPOSITIONS;

  const scores: Partial<Record<DischargeDisposition, number>> = {};
  const decisionFactors: Partial<Record<DischargeDisposition, DischargeDecisionFactor[]>> = {};
  dispositions.forEach(d => {
    const s = explicitScores?.[d] ?? genScore[d];
    scores[d] = s;
    decisionFactors[d] = explicitFactors?.[d] ?? buildFactorsForDisposition(patient, d, s);
  });

  return { scores, decisionFactors, recommended };
}
